import { Component, NO_ERRORS_SCHEMA, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { AppEnities, CasiCloseOptions, FormModes } from '../../common/constants.lib';
import { CommonModule } from '@angular/common';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { CheckBoxModule, InputsModule } from '@progress/kendo-angular-inputs';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DialogCloseResult, DialogService, DialogsModule, WindowCloseResult, WindowModule, WindowRef } from '@progress/kendo-angular-dialog';
import { NotificationModule, NotificationService } from '@progress/kendo-angular-notification';
import { KENDO_PAGER, PagerModule } from '@progress/kendo-angular-pager';
import { GridModule, PageChangeEvent, PagerPosition, PagerType } from '@progress/kendo-angular-grid';
import { MulticheckFilterComponent } from '../../filters/multicheck-filter/multicheck-filter.component';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { LoggerService } from '../../shared/logger.service';
import { MeService } from '../../auth/me.service';
import { ToastrService } from 'ngx-toastr';
import { faAddressCard, faBan, faCopy, faEdit, faPlusCircle, faTrash } from '@fortawesome/free-solid-svg-icons';
import { setTitle } from '../../common/utils.lib';
import { PlacementReview, PlacementReviewNew, PlacementReviewType } from '../../models/models.lib';
import { CompositeFilterDescriptor, distinct, filterBy, orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { PlacementReviewService } from '../placement-review.service';
import { PlacementReviewAddComponent } from '../placement-review-add.component/placement-review-add.component';
import {
  PlacementReviewDomainListComponent
} from '../placement-review-domain-list.component/placement-review-domain-list.component';
import { ModalWindowService } from '../../shared/modal-window.service';
import { PlacementReviewStudentListWin } from '../placement-review-student-list-win/placement-review-student-list-win';
import { EmailSearch } from '../../shared/components/email-search/email-search';
import { EmailSearchResult } from '../../common/obj.lib';
import { PlacementReviewAddWin } from '../placement-review-add-win/placement-review-add-win';
import { PlacementReviewDomainListWin } from '../placement-review-domain-list-win/placement-review-domain-list-win';

const { PLACEMENT_REVIEW } = AppEnities;

@Component({
  selector: 'casi-placement-review-list.component',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule, ReactiveFormsModule,
    FontAwesomeModule,
    InputsModule,
    ButtonsModule,
    DialogsModule,
    WindowModule,
    NotificationModule,
    CheckBoxModule,
    KENDO_PAGER,
    PagerModule,
    GridModule,
    MulticheckFilterComponent
  ],
  schemas: [NO_ERRORS_SCHEMA],
  templateUrl: './placement-review-list.component.html',
  styleUrl: './placement-review-list.component.scss'
})
export class PlacementReviewListComponent implements OnInit, OnDestroy {

  public view: Observable<PlacementReview[]> = new Observable<PlacementReview[]>();
  public rawData: PlacementReview[] = [];
  public filter: CompositeFilterDescriptor = { logic: "and", filters: [] };
  public gridData: PlacementReview[] = filterBy(this.rawData, this.filter);

  public currentItem: PlacementReviewType = PlacementReviewNew.create();

  public pagerTypes = ["numeric", "input"];
  public type: PagerType = "numeric";
  public info = true;
  public pageSizes = true;
  public previousNext = true;
  public position: PagerPosition = "bottom";
  public pageSize = 10;

  public formMode: FormModes = FormModes.NOTSET;

  public canUpdate: boolean = true;
  public canCreate: boolean = true;
  public canDestroy: boolean = true;
  public canView: boolean = true;

  @ViewChild("appendTo", { read: ViewContainerRef }) appendTo: ViewContainerRef | undefined;

  private _showTitle = new BehaviorSubject<string>('');
  get showTitle() {
    return this._showTitle.getValue();
  }
  set showTitle(val: string) {
    this._showTitle.next(val);
  }

  private _windowRef: WindowRef | undefined;
  private _subs: Subscription[] = [];


  constructor(
    private logger: LoggerService,
    private notificationService: NotificationService,
    private me: MeService,
    public dataService: PlacementReviewService,
    public faIcons: FaIconLibrary,
    private dialogService: DialogService,
    private toastr: ToastrService,
    private modalWindowService: ModalWindowService

  ) {
    faIcons.addIcons(
      faPlusCircle, faAddressCard, faEdit,
      faTrash,
      faBan, faCopy
    );
  }

  public sort: SortDescriptor[] = [
    { field: "organisation_name", dir: "asc" },
    { field: "discipline_name", dir: "asc" },
    { field: "evaluation_name", dir: "asc" }
  ];

  ngOnInit(): void {
    this.logger.debug('ngOnInit...');
    const { can_update, can_create, can_view, can_destroy } = this.me.allICanDo(PLACEMENT_REVIEW);
    this.canUpdate = can_update;
    this.canCreate = can_create;
    this.canView = can_view;
    this.canDestroy = can_destroy;
    // if(this.me.email)
    this.dataService.query();

    this._subs.push(this.dataService.subscribe(e => {
      this.logger.debug('ngOnInit:v: ', e);
      this.rawData = e;
      this.loadData();
    }));
  }

  ngOnDestroy(): void {
    this.logger.debug('ngOnDestroy...');
    this._subs.forEach(s => s.unsubscribe());
  }


  public pageChange(event: PageChangeEvent): void {
    this.logger.debug(`pageChange: `, event);
  }

  onClearFilters(gridName: string) {
    this.logger.debug("onClearFilters: ", gridName);
    this.filter = { logic: "and", filters: [] };
    this.loadData();
  }

  public sortChange(sort: SortDescriptor[]): void {
    this.logger.debug("sortChange: ", sort);
    this.sort = sort;
    this.loadData();
  }

  public filterChange(filter: CompositeFilterDescriptor): void {
    this.logger.debug('filterChange: filter: ', filter);
    this.filter = filter;
    this.loadData();
  }

  public distinctPrimitive(fieldName: string): any[] {
    this.logger.debug('distinctPrimitive: fieldName: ', fieldName);
    return distinct(this.gridData, fieldName).map((item: any) => item[fieldName]).filter(v => v !== null && v !== undefined && v !== '');
  }

  setActive(dataItem: any): void {
    dataItem.active = !dataItem.active;
    this.logger.debug('setActive:dataItem: ', dataItem);
    this.currentItem = PlacementReview.create(dataItem);
    this.saveActive(this.currentItem);
  }

  public onAddClick(titleBar: TemplateRef<unknown>): void {
    this.logger.debug("onAdd...");
    this.currentItem = PlacementReviewNew.create();
    this.formMode = FormModes.NEW;
    this.showTitle = setTitle(this.formMode, 'PLACEMENT REVIEW');
    this.logger.debug("onAdd:currentItem ", this.currentItem);
    this.openAddEditDialog(this.formMode, this.currentItem, titleBar);
  }

  onEditClick(dataItem: any, titleBar: TemplateRef<unknown>): void {
    this.logger.debug("onEditClick: ", dataItem);
    this.currentItem = PlacementReview.create(dataItem);
    this.formMode = FormModes.EDIT;
    this.showTitle = setTitle(this.formMode, 'PLACEMENT REVIEW');
    this.logger.debug("onEditClick:currentItem ", this.currentItem);
    this.openAddEditDialog(this.formMode, this.currentItem, titleBar);
  }

  public onStudentClick(dataItem: any): void {
    this.logger.debug("onStudentClick: ", dataItem);
    let item = PlacementReview.create(dataItem);
    this.logger.debug("onStudentClick:currentItem ", item);
    this.openStudentDialog(item);
  }

  public onEmailSearched(evnt: EmailSearchResult): void {
    this.logger.debug(`onEmailSearched: `, evnt);
  }

  private setWinInstanceInputs(dataItem: PlacementReview): void {
    if (this._windowRef) {
      const inputs = this._windowRef.content.instance;
      inputs.title = "STUDENTS ALLOCATED TO THIS REVIEW";
      inputs.placementReview = dataItem;
      this.logger.debug('setWinInstanceInputs: ', inputs);
    }
  }

  private openStudentDialog(dataItem: PlacementReview): void {
    this.logger.debug("openStudentDialog...");
    this.closeWindowRef();

    this._windowRef = this.modalWindowService.open({
      title: 'STUDENTS ALLOCATED TO THIS REVIEW',
      content: PlacementReviewStudentListWin,
      cssClass: 'full-width-window'
    });

    this.setWinInstanceInputs(dataItem);

    this._windowRef.result.subscribe((r: any) => {
      this.logger.debug('openStudentDialog:result:', r);
      // if (r instanceof DialogCloseResult) {
      if (r instanceof WindowCloseResult) {
        this.closeWindowRef();
        this.dataService.query();
      }
    });

    this._windowRef.content.instance.onClose.subscribe(() => {
      this.logger.debug('openStudentDialog:onClose');
      this.closeWindowRef();
    });
  }

  public onDomainClick(dataItem: any): void {
    this.logger.debug("onDomainClick: ", dataItem);
    let item = PlacementReview.create(dataItem);
    this.logger.debug("onDomainClick:currentItem ", item);
    this.openDomainDialog(item);
  }

  private openDomainDialog(dataItem: PlacementReview): void {
    this.logger.debug("openDomainDialog...");
    this.closeWindowRef();

    this._windowRef = this.modalWindowService.open({
      title: 'CONFIRM OPTIONAL DOMAINS',
      content: PlacementReviewDomainListWin,
      cssClass: 'half-width-window'
    });

    // const dialogRef = this.dialogService.open({
    //   content: PlacementReviewDomainListComponent,
    //   minWidth: 320,
    //   width: '50vw',
    // });

    this.setWinInstanceInputs(dataItem);
    // const contentRef = dialogRef.content.instance as PlacementReviewDomainListComponent;
    // contentRef.title = "CONFIRM OPTIONAL DOMAINS";
    // contentRef.placementReview = dataItem;

    this._windowRef.result.subscribe((r: any) => {
      this.logger.debug('openStudentDialog:result:', r);
      // if (r instanceof DialogCloseResult) {
      if (r instanceof WindowCloseResult) {
        this.closeWindowRef();
        this.dataService.query();
      }
    });

    this._windowRef.content.instance.onClose.subscribe(() => {
      this.logger.debug('openStudentDialog:onClose');
      this.closeWindowRef();
    });

    // dialogRef.result.subscribe((r: any) => {
    //   this.logger.debug('openStudentDialog:result:', r);
    //   if (r instanceof DialogCloseResult) {
    //     return;
    //   }
    //   this.dataService.query();
    // });
  }

  private setAddEditWinInstanceInputs(dataItem: PlacementReviewType, mode: FormModes): void {
    if (this._windowRef) {
      const inputs = this._windowRef.content.instance;
      inputs.title = setTitle(mode, 'PLACEMENT REVIEW');
      inputs.placementReview = dataItem;
      inputs.mode = mode;
      this.logger.debug('setAddEditWinInstanceInputs: ', inputs);
    }
  }

  private openAddEditDialog(mode: FormModes, dataItem: PlacementReviewType, titleBar: TemplateRef<unknown>): void {
    this.logger.debug("openAddEditDialog...");
    this.closeWindowRef();

    this._windowRef = this.modalWindowService.open({
      titleBarContent: titleBar,
      content: PlacementReviewAddWin,
      minWidth: 320,
      width: 1000
    });
    // const dialogRef = this.dialogService.open({
    //   content: PlacementReviewAddComponent,
    //   minWidth: 320,
    //   width: 1000,
    // });

    this.setAddEditWinInstanceInputs(dataItem, mode);
    // const contentRef = dialogRef.content.instance as PlacementReviewAddComponent;
    // contentRef.title = setTitle(mode, 'PLACEMENT REVIEW');
    // contentRef.placementReview = dataItem;
    // contentRef.mode = mode;

    this._windowRef.content.instance.onSubmit.subscribe((formData: FormGroup) => {
      this.logger.debug('openAddEditDialog:formData: ', formData);
      this.handleClose(formData.value, mode);
    });

    this._windowRef.result.subscribe((r: any) => {
      this.logger.debug('openAddEditDialog:result:', r);
      // if (r instanceof DialogCloseResult) {
      if (r instanceof WindowCloseResult) {
        this.closeWindowRef();
        // this.dataService.query();
      }
    });

    this._windowRef.content.instance.onClose.subscribe(() => {
      this.logger.debug('openAddEditDialog:onClose');
      this.closeWindowRef();
    });

    // dialogRef.result.subscribe((r: any) => {
    //   this.logger.debug('openAddEditDialog:result:', r);
    //   if (r instanceof DialogCloseResult) {
    //     return;
    //   }
    //   if (r.text === CasiCloseOptions.Saved) {
    //     this.logger.debug("openAddEditDialog:saved... ", contentRef.editForm.value);
    //     this.handleClose(contentRef.editForm.value, mode);
    //   }
    // });
  }

  private handleClose(data: any, mode: FormModes): void {
    this.logger.debug('handleClose...', data, mode);
    this.save(data, mode);
  }

  private save(placementReview: any, mode: FormModes): void {
    this.logger.debug('save...', placementReview, mode);
    if (mode === FormModes.EDIT) {
      this.dataService.update(placementReview.id, placementReview)
        .then(resp => {
          this.logger.debug('save:update:resp: ', resp);
          this.notify("Placement Review Updated");
          this.closeWindowRef();
          // if (this.me.email)
          this.dataService.query();
        })
        .catch(err => {
          this.logger.error('save:update:err: ', err);
          this.notify("Error Updating Placement Review", true);
        });
    } else if (mode === FormModes.NEW) {
      this.dataService.add(placementReview)
        .then(resp => {
          this.logger.debug('save:add:resp: ', resp);
          this.notify("Placement Review Added");
          this.closeWindowRef();
          // if (this.me.email)
          this.dataService.query();
        })
        .catch(err => {
          this.logger.error('save:add:err: ', err);
          this.notify("Error Creating Placement Review", true);
        });
    }
  }

  private saveActive(placementReview: any): void {
    this.logger.debug('saveActive...', placementReview);
    this.dataService.update(placementReview.id, placementReview)
      .then(resp => {
        this.logger.debug('saveActive:update:resp: ', resp);
        this.notify(placementReview.active ? "Placement Review Activated" : "Placement Review Deactivated");
        this.closeWindowRef();
        // if (this.me.email)
        this.dataService.query();
      })
      .catch(err => {
        this.logger.error('saveActive:update:err: ', err);
        this.notify("Error Updating Evaluation", true);
      });
  }

  private notify(msg: string, error: boolean = false): void {
    this.logger.debug("notify", msg);
    if (error)
      this.toastr.error(msg);
    else
      this.toastr.success(msg);
  }

  private closeWindowRef(): void {
    if (this._windowRef) {
      this.modalWindowService.close(this._windowRef);
      this._windowRef = undefined;
    }
  }

  private loadData(): void {
    this.gridData = orderBy(filterBy(this.rawData, this.filter), this.sort);
    this.pageSize = this.gridData.length;
  }
}

